/**
 * Enhances the Shopify Forms app embed (`shopify-forms-embed`).
 *
 * Two responsibilities, both hooked to the same "shadow root is ready" lifecycle:
 *   1. Inject theme CSS into the form's (open) shadow DOM.
 *   2. Wire optional external submit buttons to the form's built-in submit.
 *
 * Contract with the theme:
 *   - External button carries `data-shopify-form-submit="forms-root-<id>"`
 *     (see snippets/button-submit.liquid).
 *   - The form root carries `data-forms-id="forms-root-<id>"`.
 *   - Matching is an exact string equality.
 *
 * NOTE: this integration relies on undocumented internals of the Shopify Forms
 * app (the `button[type="submit"]` element and hashed class names such as
 * `_formFieldset`, `_formFieldContainer` and `_formError`). The app has no public
 * submit API, so these may change between app builds and silently break. Keep
 * this isolated here.
 */
(function () {
  var TAG = 'shopify-forms-embed';
  var BTN_SELECTOR = '[data-shopify-form-submit]';
  // The Forms app's rendered <form>. Its removal from the shadow DOM marks the
  // end of the submit lifecycle (the app replaces it with a confirmation).
  var FORM_SELECTOR = 'form[class^="_formFieldset"]';

  var SHADOW_CSS = `
    button[type="submit"] {margin-top: 12px; text-transform: uppercase; }
    button[type="submit"]:hover {font-weight: bold;}
    input, textarea, select, label, p, a, h1, h2, h3, h4, h5, h6 {
      text-transform: uppercase;
    }
    div[class^="_textBody"] > *, span[class^="_textBody"], p[class^="_textBody"] {
      line-height: 1.090909;
    }
    section[class^="_formContainer"] {margin: 0;}
    section[class^="_formBannerContainer"] {margin: 0;}
    section[class^="_formHeader"] {margin: 0;}
    div[class^="_formDisclaimer"] p {font: var(--forms-body-font); line-height: 1.090909;}
    form[class^="_formFieldset"] {margin-bottom: 24px; padding: 0;}
    div[class^="_formFieldContainer"] input,
    div[class^="_formFieldContainer"] input:focus-visible,
    div[class^="_formFieldContainer"] textarea,
    div[class^="_formFieldContainer"] textarea:focus-visible,
    div[class^="_formFieldContainer"] select,
    div[class^="_formFieldContainer"] select:focus-visible
      { border: var(--style-border-width-inputs) solid var(--color-input-border); border-width: 0 0 var(--style-border-width-inputs);}
    div input[aria-invalid="true"] {box-shadow: none;}
    output[class^="_formError"] { margin-top: -0.5rem; }
    h2[class^="_textHeading"]:empty {display: none;}
    h2[class^="_formBannerHeading"] {line-height: 1.090909 !important; margin: 0 0 24px !important;}

    @media screen and (max-width: 490px) {
      div[class^="_container"] {display:block;}
    }

    /* Hidden only when an external submit button is wired to this host.
       Driven by a host attribute so it survives React re-renders. */
    :host([data-external-submit]) button[type="submit"] { display: none; }
  `;

  function theme(host) {
    var root = host.shadowRoot;
    if (!root || host.dataset.themedForm === '1') return;
    try {
      if ('adoptedStyleSheets' in root && typeof CSSStyleSheet === 'function') {
        var sheet = new CSSStyleSheet();
        sheet.replaceSync(SHADOW_CSS);
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
      } else {
        root.appendChild(Object.assign(document.createElement('style'), { textContent: SHADOW_CSS }));
      }
      host.dataset.themedForm = '1';
    } catch (e) {
      /* styling is non-critical; never break the form */
    }
  }

  function wireExternalButtons(host) {
    if (host.dataset.externalSubmitWired === '1') return;

    var rootEl = host.closest('[data-forms-id]');
    var formsId = rootEl && rootEl.getAttribute('data-forms-id');
    if (!formsId) return;

    var buttons;
    try {
      var selector = BTN_SELECTOR + '[data-shopify-form-submit="' + CSS.escape(formsId) + '"]';
      buttons = document.querySelectorAll(selector);
    } catch (e) {
      return;
    }
    if (!buttons.length) return; // no external buttons -> keep the built-in submit visible

    buttons.forEach(function (btn) {
      if (btn.dataset.formSubmitBound === '1') return;
      btn.dataset.formSubmitBound = '1';
      // No preventDefault: by contract there is no native <form> with this id,
      // so the built-in form is submitted via a synthetic click on its own
      // submit button (the React form has no public submit API).
      btn.addEventListener('click', function () {
        var target = host.shadowRoot && host.shadowRoot.querySelector('button[type="submit"]');
        if (!target) return;
        target.click();
        watchSubmission(host, formsId);
      });
    });

    // Hide the built-in submit via the injected CSS rule above.
    host.setAttribute('data-external-submit', '');
    host.dataset.externalSubmitWired = '1';
    // Buttons added to the DOM after this point are intentionally not wired (no-op).
  }

  // The form is gone, so the external button no longer points at anything:
  // turn each matching button into a link (when configured) or hide it.
  function resolveExternalButtons(formsId) {
    var selector;
    try {
      selector = BTN_SELECTOR + '[data-shopify-form-submit="' + CSS.escape(formsId) + '"]';
    } catch (e) {
      return;
    }
    document.querySelectorAll(selector).forEach(function (btn) {
      var href = btn.dataset.successHref;
      var text = btn.dataset.successText;
      if (href && text) {
        var link = document.createElement('a');
        link.className = btn.className;
        link.setAttribute('style', btn.getAttribute('style') || '');
        link.style.textDecoration = 'none';
        link.style.color = 'inherit';
        link.href = href;
        link.textContent = text;
        btn.replaceWith(link);
      } else {
        // It no longer points at a live form. Hide it but keep its layout box,
        // so the space it occupied is preserved and neighbors don't shift.
        btn.style.visibility = 'hidden';
      }
    });
  }

  // The only success signal we get is the form element disappearing: it stays
  // mounted while filling in / on validation errors, and is replaced once the
  // submit completes. `submitWatching` keeps a single one-shot watcher per host.
  function watchSubmission(host, formsId) {
    if (host.dataset.submitWatching) return;
    var root = host.shadowRoot;
    if (!root) return;
    host.dataset.submitWatching = 'pending';

    function formGone() {
      return !root.querySelector(FORM_SELECTOR);
    }
    function resolve(mo) {
      if (host.dataset.submitWatching === 'done') return; // one-shot
      host.dataset.submitWatching = 'done';
      if (mo) mo.disconnect();
      resolveExternalButtons(formsId);
    }

    // Guard the unlikely instant-success race (replacement before first mutation).
    if (formGone()) return resolve(null);

    var mo = new MutationObserver(function () {
      if (formGone()) resolve(mo);
    });
    mo.observe(root, { childList: true, subtree: true });
  }

  function enhance(host) {
    theme(host);
    wireExternalButtons(host);
  }

  function whenReady(host) {
    if (host.shadowRoot) return enhance(host);
    var mo = new MutationObserver(function () {
      if (host.shadowRoot) {
        enhance(host);
        mo.disconnect();
      }
    });
    mo.observe(host, { childList: true, subtree: true });
  }

  function scan(node) {
    if (node.nodeType !== 1) return;
    if (node.matches && node.matches(TAG)) whenReady(node);
    if (node.querySelectorAll) node.querySelectorAll(TAG).forEach(whenReady);
  }

  var container = document.getElementById('MainContent') || document.documentElement;
  scan(container);

  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) scan(added[j]);
    }
  }).observe(container, { childList: true, subtree: true });
})();
